import { XoloDogAnimation } from './XoloDogAnimation';
import { cn } from '@/lib/utils';
import type { CelebrationType, XoloState } from '@/lib/delight/types';
import { CELEBRATION_CONFIGS } from '@/lib/delight/types';

export interface DelightToastProps {
  type: CelebrationType;
  message: string;
  subtitle?: string;
  xoloState?: XoloState;
  className?: string;
}

/**
 * DelightToast - Custom content for celebration toasts
 *
 * Used with Sonner's toast.custom() function.
 * Displays Xolo mascot with celebration message.
 *
 * PRIVACY: Messages must be generic - no client names or PII.
 */
export function DelightToast({ type, message, subtitle, xoloState, className }: DelightToastProps) {
  const config = CELEBRATION_CONFIGS[type];
  const finalXoloState = xoloState || config.xoloState;

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-3 rounded-lg border shadow-lg min-w-[300px] max-w-[400px]',
        config.bgColor,
        'border-border',
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <XoloDogAnimation state={finalXoloState} size="md" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-lg" aria-hidden="true">
            {config.icon}
          </span>
          <p className="font-semibold text-sm text-foreground">{config.title}</p>
        </div>
        <p className="text-sm text-foreground/80">{message}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
      </div>
    </div>
  );
}

export type { CelebrationType };
